import { useCallback, useMemo } from 'react'
import { useContactBulkActions } from 'app/components/contacts/useContactBulkActions'
import type { BulkActionValue, BulkActionsConfig, BulkProgressReporter } from 'app/components/table/bulkActions/types'
import type { ContactJson } from 'app/types/entities'
import type { TreatmentDestination } from 'app/types/treatment'
import { STAGE_ACTION_ID, SetStage, applyStageChange, treatmentBulkActionList } from './treatmentBulkActions'
import { TreatmentRow } from './useTreatmentBoard'

/**
 * Bulk actions for the unscheduled-treatment table.
 *
 * The stage move is this table's own: it is the one thing the board next door
 * does by dragging and a table has nowhere to drag to. Everything else is the
 * contacts table's set, narrowed to the actions that mean something with a
 * case in hand, so a tag applied here behaves exactly as it does there.
 */
export function useTreatmentBulkActions(
  setStage: SetStage,
  stages?: TreatmentDestination[],
): BulkActionsConfig<TreatmentRow> {
  const contacts = useContactBulkActions()

  const actions = useMemo(() => treatmentBulkActionList(contacts.actions, stages), [contacts.actions, stages])

  const apply = useCallback(
    (actionId: string, value: BulkActionValue, rows: TreatmentRow[], onProgress?: BulkProgressReporter) => {
      if (actionId === STAGE_ACTION_ID) return applyStageChange(rows, value as string, setStage, onProgress)
      // A treatment row carries the fields those actions read (id, name, cell
      // phone, location, pipelines) under the same names a contact does, since
      // it is built from one. It is not a whole contact, which is why this is a
      // cast rather than a type the two share: the fields the contacts adapter
      // reads and a row does not carry (patient ids, lead status) belong only
      // to actions this table does not offer.
      return contacts.apply(actionId, value, rows as unknown as ContactJson[], onProgress)
    },
    [contacts, setStage],
  )

  return { actions, apply }
}
